import React, {
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { twMerge } from 'tailwind-merge';

import OutsideClickWrapper from '@/components/OutsideClickWrapper';
import AuthenticationModal, {
  LoginSignupModalViewMode,
} from '@/features/authentication';
import { useUserQuotasContext } from '@/features/userQuotas/provider';
import { ReactSetState } from '@/models/reactTypes';
import { unimplementedFunction } from '@/utils/unimplementedFunction';

interface IContentWithProps {
  content: ReactNode;
  onCloseEvent: () => void;
}
export type ModalContextType = {
  modalContent: ReactNode | IContentWithProps;
  setModalContent: ReactSetState<ReactNode | IContentWithProps>;
  /** Will open the auth modal */
  openAuthModal: (initial?: LoginSignupModalViewMode) => void;
  clearModalContent: () => void;
};

export const ModalContext = React.createContext<ModalContextType>({
  clearModalContent: unimplementedFunction,
  modalContent: null,
  openAuthModal: unimplementedFunction,
  setModalContent: unimplementedFunction,
});

export const ModalProvider: React.FC<PropsWithChildren> = ({
  children = undefined,
}) => {
  const [modalContent, setModalContent] = useState<
    ReactNode | IContentWithProps
  >(null);

  const { refreshQuotas } = useUserQuotasContext();

  useEffect(() => {
    const abortController = new AbortController();

    if (modalContent === null) {
      refreshQuotas(abortController.signal);
    }

    return () => {
      abortController.abort();
    };
  }, [modalContent, refreshQuotas]);

  /**
   * Opens the auth modal with the provided initial state
   *   - Defaults to "login"
   */
  const openAuthModal = useCallback(
    (initial?: LoginSignupModalViewMode) => {
      setModalContent(<AuthenticationModal initial={initial ?? 'login'} />);
    },
    [setModalContent]
  );

  const clearModalContent = useCallback(() => {
    setModalContent(null);
  }, []);

  const value: ModalContextType = useMemo(() => {
    return { clearModalContent, modalContent, openAuthModal, setModalContent };
  }, [clearModalContent, modalContent, openAuthModal]);

  const content = useMemo(() => {
    if (React.isValidElement(modalContent)) {
      return modalContent;
    }
    if ((modalContent as IContentWithProps)?.content) {
      return (
        <OutsideClickWrapper
          onOutsideClick={(modalContent as IContentWithProps)?.onCloseEvent}
        >
          {(modalContent as IContentWithProps)?.content}
        </OutsideClickWrapper>
      );
    }
    return undefined;
  }, [modalContent]);

  return (
    <ModalContext.Provider value={value}>
      {content && (
        <div
          className={twMerge(
            'fixed left-0 top-0 z-[1000] flex h-[100vh] w-screen items-center justify-center',
            'bg-black bg-opacity-70'
          )}
        >
          {content}
        </div>
      )}
      {children}
    </ModalContext.Provider>
  );
};

/** Hook for easily accessing modal context
 *   - To display a modal simply, set the modal content to you desired JSX. ex: setModalContent(<>modal jsx here</>)
 *   - To close the current modal, just set the modal content to null. ex: setModalContent(null)
 */
export const useModalContext = (): ModalContextType => {
  const { setModalContent, modalContent, openAuthModal, clearModalContent } =
    useContext(ModalContext);

  return { clearModalContent, modalContent, openAuthModal, setModalContent };
};
